import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import { postsAPI, membersAPI, aboutAPI, contactAPI, creditsAPI, carouselAPI, artAPI } from '../services/api';

const Admin = () => {
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const { toast } = useToast();

  // State for managing different content
  const [posts, setPosts] = useState([]);
  const [members, setMembers] = useState([]);
  const [aboutContent, setAboutContent] = useState({ title: '', content: '' });
  const [contactInfo, setContactInfo] = useState([]);
  const [credits, setCredits] = useState([]);
  const [carouselItems, setCarouselItems] = useState([]);
  const [artPieces, setArtPieces] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form states
  const [newPost, setNewPost] = useState({ content: '', image: null });
  const [newMember, setNewMember] = useState({ name: '', country: '', photo: null, twitchUrl: '' });
  const [editingMember, setEditingMember] = useState(null);
  const [editingAbout, setEditingAbout] = useState(false);
  const [aboutForm, setAboutForm] = useState({ title: '', content: '' });
  const [newContact, setNewContact] = useState({ title: '', content: '', link: '' });
  const [newCredit, setNewCredit] = useState({ name: '', role: '', description: '', photo: null });
  const [editingCredit, setEditingCredit] = useState(null);
  const [newCarousel, setNewCarousel] = useState({ title: '', description: '', image: null, order: 1 });
  const [editingCarousel, setEditingCarousel] = useState(null);
  const [newArt, setNewArt] = useState({ title: '', description: '', image: null, artist_name: '', artist_twitter: '' });
  const [editingArt, setEditingArt] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      fetchAllData();
    }
  }, [isAuthenticated]);

  const fetchAllData = async () => {
    try {
      const [postsRes, membersRes, aboutRes, contactsRes, creditsRes, carouselRes, artRes] = await Promise.all([
        postsAPI.getAll(),
        membersAPI.getAll(),
        aboutAPI.get(),
        contactAPI.getAll(),
        creditsAPI.getAll(),
        carouselAPI.getAll(),
        artAPI.getAll()
      ]);

      setPosts(postsRes.data);
      setMembers(membersRes.data);
      setAboutContent(aboutRes.data);
      setAboutForm(aboutRes.data);
      setContactInfo(contactsRes.data);
      setCredits(creditsRes.data);
      setCarouselItems(carouselRes.data);
      setArtPieces(artRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: "Error",
        description: "Failed to load data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64String = event.target.result;
        if (type === 'post') {
          setNewPost(prev => ({ ...prev, image: base64String }));
        } else if (type === 'member') {
          setNewMember(prev => ({ ...prev, photo: base64String }));
        } else if (type === 'credit') {
          setNewCredit(prev => ({ ...prev, photo: base64String }));
        } else if (type === 'carousel') {
          setNewCarousel(prev => ({ ...prev, image: base64String }));
        } else if (type === 'art') {
          setNewArt(prev => ({ ...prev, image: base64String }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreatePost = async () => {
    if (!newPost.content.trim()) {
      toast({
        title: "Error",
        description: "Post content is required",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await postsAPI.create(newPost, getAuthHeaders());
      setPosts([response.data, ...posts]);
      setNewPost({ content: '', image: null });
      toast({
        title: "Success",
        description: "Post created successfully!"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create post",
        variant: "destructive"
      });
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      await postsAPI.delete(postId, getAuthHeaders());
      setPosts(posts.filter(post => post.id !== postId));
      toast({
        title: "Success",
        description: "Post deleted successfully!"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete post",
        variant: "destructive"
      });
    }
  };

  const handleCreateMember = async () => {
    if (!newMember.name.trim() || !newMember.country.trim()) {
      toast({
        title: "Error",
        description: "Name and country are required",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await membersAPI.create(newMember, getAuthHeaders());
      setMembers([...members, response.data]);
      setNewMember({ name: '', country: '', photo: null, twitchUrl: '' });
      toast({
        title: "Success",
        description: "Member added successfully!"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add member",
        variant: "destructive"
      });
    }
  };

  const handleEditMember = (member) => {
    setEditingMember(member.id);
    setNewMember({
      name: member.name,
      country: member.country,
      photo: member.photo,
      twitchUrl: member.twitchUrl || ''
    });
  };

  const handleUpdateMember = async () => {
    try {
      const response = await membersAPI.update(editingMember, newMember, getAuthHeaders());
      setMembers(members.map(member => member.id === editingMember ? response.data : member));
      setEditingMember(null);
      setNewMember({ name: '', country: '', photo: null, twitchUrl: '' });
      toast({
        title: "Success",
        description: "Member updated successfully!"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update member",
        variant: "destructive"
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingMember(null);
    setNewMember({ name: '', country: '', photo: null, twitchUrl: '' });
  };

  const handleDeleteMember = async (memberId) => {
    try {
      await membersAPI.delete(memberId, getAuthHeaders());
      setMembers(members.filter(member => member.id !== memberId));
      toast({
        title: "Success",
        description: "Member deleted successfully!"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete member",
        variant: "destructive"
      });
    }
  };

  const handleUpdateAbout = async () => {
    try {
      const response = await aboutAPI.update(aboutForm, getAuthHeaders());
      setAboutContent(response.data);
      setEditingAbout(false);
      toast({
        title: "Success",
        description: "About content updated successfully!"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update about content",
        variant: "destructive"
      });
    }
  };

  const handleCreateContact = async () => {
    if (!newContact.title.trim() || !newContact.content.trim()) {
      toast({
        title: "Error",
        description: "Title and content are required",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await contactAPI.create(newContact, getAuthHeaders());
      setContactInfo([...contactInfo, response.data]);
      setNewContact({ title: '', content: '', link: '' });
      toast({
        title: "Success",
        description: "Contact item added successfully!"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add contact item",
        variant: "destructive"
      });
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await contactAPI.delete(contactId, getAuthHeaders());
      setContactInfo(contactInfo.filter(contact => contact.id !== contactId));
      toast({
        title: "Success",
        description: "Contact item deleted successfully!"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete contact item",
        variant: "destructive"
      });
    }
  };

  // Credits handlers
  const handleCreateCredit = async () => {
    if (!newCredit.name.trim() || !newCredit.role.trim()) {
      toast({
        title: "Error",
        description: "Name and role are required",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await creditsAPI.create(newCredit, getAuthHeaders());
      setCredits([...credits, response.data]);
      setNewCredit({ name: '', role: '', description: '', photo: null });
      toast({
        title: "Success",
        description: "Credit added successfully!"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add credit",
        variant: "destructive"
      });
    }
  };

  const handleEditCredit = (credit) => {
    setEditingCredit(credit.id);
    setNewCredit({
      name: credit.name,
      role: credit.role,
      description: credit.description || '',
      photo: credit.photo || null
    });
  };

  const handleUpdateCredit = async () => {
    try {
      const response = await creditsAPI.update(editingCredit, newCredit, getAuthHeaders());
      setCredits(credits.map(credit => credit.id === editingCredit ? response.data : credit));
      setEditingCredit(null);
      setNewCredit({ name: '', role: '', description: '', photo: null });
      toast({
        title: "Success",
        description: "Credit updated successfully!"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update credit",
        variant: "destructive"
      });
    }
  };

  const handleCancelCreditEdit = () => {
    setEditingCredit(null);
    setNewCredit({ name: '', role: '', description: '', photo: null });
  };

  const handleDeleteCredit = async (creditId) => {
    try {
      await creditsAPI.delete(creditId, getAuthHeaders());
      setCredits(credits.filter(credit => credit.id !== creditId));
      toast({
        title: "Success",
        description: "Credit deleted successfully!"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete credit",
        variant: "destructive"
      });
    }
  };

  // Carousel Management Functions
  const handleCreateCarousel = async () => {
    if (!newCarousel.title.trim() || !newCarousel.image) {
      toast({
        title: "Error",
        description: "Title and image are required",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await carouselAPI.create(newCarousel, getAuthHeaders());
      setCarouselItems([...carouselItems, response.data]);
      setNewCarousel({ title: '', description: '', image: null, order: 1 });
      toast({
        title: "Success",
        description: "Carousel item created successfully!"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create carousel item",
        variant: "destructive"
      });
    }
  };

  const handleUpdateCarousel = async () => {
    if (!editingCarousel) return;

    try {
      const response = await carouselAPI.update(editingCarousel.id, editingCarousel, getAuthHeaders());
      setCarouselItems(carouselItems.map(item => 
        item.id === editingCarousel.id ? response.data : item
      ));
      setEditingCarousel(null);
      toast({
        title: "Success",
        description: "Carousel item updated successfully!"
      });
    } catch (error) {
      toast({
        title: "Error", 
        description: "Failed to update carousel item",
        variant: "destructive"
      });
    }
  };

  const handleDeleteCarousel = async (carouselId) => {
    try {
      await carouselAPI.delete(carouselId, getAuthHeaders());
      setCarouselItems(carouselItems.filter(item => item.id !== carouselId));
      toast({
        title: "Success",
        description: "Carousel item deleted successfully!"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete carousel item",
        variant: "destructive"
      });
    }
  };

  // Art handlers
  const handleCreateArt = async () => {
    if (!newArt.title.trim() || !newArt.artist_name.trim() || !newArt.artist_twitter.trim() || !newArt.image) {
      toast({
        title: "Error",
        description: "Title, image, artist name, and Twitter handle are required",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await artAPI.create(newArt, getAuthHeaders());
      setArtPieces([response.data, ...artPieces]);
      setNewArt({ title: '', description: '', image: null, artist_name: '', artist_twitter: '' });
      toast({
        title: "Success",
        description: "Art piece added successfully!"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add art piece",
        variant: "destructive"
      });
    }
  };

  const handleEditArt = (art) => {
    setEditingArt(art.id);
    setNewArt({
      title: art.title,
      description: art.description || '',
      image: art.image,
      artist_name: art.artist_name,
      artist_twitter: art.artist_twitter
    });
  };

  const handleUpdateArt = async () => {
    try {
      const response = await artAPI.update(editingArt, newArt, getAuthHeaders());
      setArtPieces(artPieces.map(art => art.id === editingArt ? response.data : art));
      setEditingArt(null);
      setNewArt({ title: '', description: '', image: null, artist_name: '', artist_twitter: '' });
      toast({
        title: "Success",
        description: "Art piece updated successfully!"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update art piece",
        variant: "destructive"
      });
    }
  };

  const handleCancelArtEdit = () => {
    setEditingArt(null);
    setNewArt({ title: '', description: '', image: null, artist_name: '', artist_twitter: '' });
  };

  const handleDeleteArt = async (artId) => {
    try {
      await artAPI.delete(artId, getAuthHeaders());
      setArtPieces(artPieces.filter(art => art.id !== artId));
      toast({
        title: "Success",
        description: "Art piece deleted successfully!"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete art piece",
        variant: "destructive"
      });
    }
  };

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-600 mx-auto mb-4"></div>
          <p className="text-xl">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-red-500 to-red-700 bg-clip-text text-transparent">
            Admin Panel
          </h1>
          <p className="text-gray-300">Manage your server's content and community</p>
        </div>

        <Tabs defaultValue="posts" className="space-y-6">
          <TabsList className="grid w-full grid-cols-7 bg-gray-800 border border-red-600/30">
            <TabsTrigger value="posts" className="data-[state=active]:bg-red-600">Posts</TabsTrigger>
            <TabsTrigger value="carousel" className="data-[state=active]:bg-red-600">Carousel</TabsTrigger>
            <TabsTrigger value="members" className="data-[state=active]:bg-red-600">Members</TabsTrigger>
            <TabsTrigger value="about" className="data-[state=active]:bg-red-600">About</TabsTrigger>
            <TabsTrigger value="contact" className="data-[state=active]:bg-red-600">Contact</TabsTrigger>
            <TabsTrigger value="credits" className="data-[state=active]:bg-red-600">Credits</TabsTrigger>
            <TabsTrigger value="art" className="data-[state=active]:bg-red-600">Art</TabsTrigger>
          </TabsList>

          {/* Posts Management */}
          <TabsContent value="posts">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-gray-800 border-red-600/30">
                <CardHeader>
                  <CardTitle className="text-white">Create New Post</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <textarea
                    value={newPost.content}
                    onChange={(e) => setNewPost(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="What's happening on ISMP?"
                    className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white resize-none"
                    rows={4}
                  />
                  <div>
                    <label className="block text-white mb-2">Upload Image (optional)</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'post')}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-red-600 file:text-white hover:file:bg-red-700"
                    />
                  </div>
                  {newPost.image && (
                    <img src={newPost.image} alt="Preview" className="w-full max-h-48 object-cover rounded-lg" />
                  )}
                  <Button onClick={handleCreatePost} className="w-full bg-red-600 hover:bg-red-700">
                    Create Post
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-red-600/30">
                <CardHeader>
                  <CardTitle className="text-white">Recent Posts</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {posts.map((post) => (
                      <div key={post.id} className="bg-gray-700 p-4 rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <p className="text-gray-300 text-sm">{new Date(post.timestamp).toLocaleString()}</p>
                          <Button
                            onClick={() => handleDeletePost(post.id)}
                            variant="destructive"
                            size="sm"
                          >
                            Delete
                          </Button>
                        </div>
                        <p className="text-white mb-2">{post.content}</p>
                        {post.image && (
                          <img src={post.image} alt="Post" className="w-full max-h-32 object-cover rounded" />
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Carousel Management */}
          <TabsContent value="carousel" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-gray-800 border-red-600/30">
                <CardHeader>
                  <CardTitle className="text-white">
                    {editingCarousel ? 'Edit Carousel Item' : 'Add New Carousel Item'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <input
                    type="text"
                    value={editingCarousel ? editingCarousel.title : newCarousel.title}
                    onChange={(e) => editingCarousel 
                      ? setEditingCarousel(prev => ({ ...prev, title: e.target.value }))
                      : setNewCarousel(prev => ({ ...prev, title: e.target.value }))
                    }
                    placeholder="Carousel title"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  />
                  <textarea
                    value={editingCarousel ? editingCarousel.description : newCarousel.description}
                    onChange={(e) => editingCarousel 
                      ? setEditingCarousel(prev => ({ ...prev, description: e.target.value }))
                      : setNewCarousel(prev => ({ ...prev, description: e.target.value }))
                    }
                    placeholder="Description (optional)"
                    className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white resize-none"
                    rows={3}
                  />
                  <input
                    type="number"
                    value={editingCarousel ? editingCarousel.order : newCarousel.order}
                    onChange={(e) => editingCarousel 
                      ? setEditingCarousel(prev => ({ ...prev, order: parseInt(e.target.value) }))
                      : setNewCarousel(prev => ({ ...prev, order: parseInt(e.target.value) }))
                    }
                    placeholder="Display order"
                    min="1"
                    max="10"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  />
                  <div>
                    <label className="block text-white mb-2">Carousel Image (required)</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'carousel')}
                      className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg text-white file:bg-red-600 file:text-white file:border-none file:rounded file:px-4 file:py-2 file:mr-4"
                    />
                    {(newCarousel.image || editingCarousel?.image) && (
                      <div className="mt-2">
                        <img
                          src={editingCarousel ? editingCarousel.image : newCarousel.image}
                          alt="Carousel preview"
                          className="w-full h-32 object-cover rounded-lg border border-gray-600"
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex space-x-3">
                    <Button
                      onClick={editingCarousel ? handleUpdateCarousel : handleCreateCarousel}
                      className="flex-1 bg-red-600 hover:bg-red-700"
                    >
                      {editingCarousel ? 'Update Carousel Item' : 'Create Carousel Item'}
                    </Button>
                    {editingCarousel && (
                      <Button
                        onClick={() => setEditingCarousel(null)}
                        variant="outline"
                        className="border-gray-600 text-gray-300 hover:bg-gray-700"
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-red-600/30">
                <CardHeader>
                  <CardTitle className="text-white">Carousel Items ({carouselItems.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {carouselItems.sort((a, b) => a.order - b.order).map((item) => (
                      <div key={item.id} className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                        <div className="flex items-start space-x-4">
                          <img
                            src={item.image}
                            alt={item.title}
                            className="w-20 h-12 object-cover rounded border border-gray-600 flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <h4 className="text-white font-medium truncate">{item.title}</h4>
                            {item.description && (
                              <p className="text-gray-400 text-sm mt-1 line-clamp-2">{item.description}</p>
                            )}
                            <p className="text-red-400 text-xs mt-1">Order: {item.order}</p>
                          </div>
                          <div className="flex space-x-2">
                            <Button
                              onClick={() => setEditingCarousel(item)}
                              size="sm"
                              variant="outline"
                              className="border-gray-600 text-gray-300 hover:bg-gray-600"
                            >
                              Edit
                            </Button>
                            <Button
                              onClick={() => handleDeleteCarousel(item.id)}
                              size="sm"
                              variant="outline"
                              className="border-red-600 text-red-400 hover:bg-red-600 hover:text-white"
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {carouselItems.length === 0 && (
                      <div className="text-center py-8">
                        <p className="text-gray-400">No carousel items yet</p>
                        <p className="text-gray-500 text-sm mt-1">Add your first carousel item to display on the homepage</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Members Management */}
          <TabsContent value="members">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-gray-800 border-red-600/30">
                <CardHeader>
                  <CardTitle className="text-white">
                    {editingMember ? 'Edit Member' : 'Add New Member'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <input
                    type="text"
                    value={newMember.name}
                    onChange={(e) => setNewMember(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Member name"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  />
                  <input
                    type="text"
                    value={newMember.country}
                    onChange={(e) => setNewMember(prev => ({ ...prev, country: e.target.value }))}
                    placeholder="Country"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  />
                  <input
                    type="text"
                    value={newMember.twitchUrl}
                    onChange={(e) => setNewMember(prev => ({ ...prev, twitchUrl: e.target.value }))}
                    placeholder="Twitch URL (optional)"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  />
                  <div>
                    <label className="block text-white mb-2">Upload Photo</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'member')}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-red-600 file:text-white hover:file:bg-red-700"
                    />
                  </div>
                  {newMember.photo && (
                    <img src={newMember.photo} alt="Preview" className="w-20 h-20 rounded-full object-cover" />
                  )}
                  <div className="flex space-x-2">
                    {editingMember ? (
                      <>
                        <Button onClick={handleUpdateMember} className="flex-1 bg-green-600 hover:bg-green-700">
                          Update Member
                        </Button>
                        <Button onClick={handleCancelEdit} variant="outline" className="flex-1">
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button onClick={handleCreateMember} className="w-full bg-red-600 hover:bg-red-700">
                        Add Member
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-red-600/30">
                <CardHeader>
                  <CardTitle className="text-white">Current Members</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {members.map((member) => (
                      <div key={member.id} className="bg-gray-700 p-4 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="relative">
                              <img src={member.photo} alt={member.name} className="w-12 h-12 rounded-full" />
                              {member.isLive && (
                                <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-pulse">
                                  <div className="w-2 h-2 bg-white rounded-full m-1"></div>
                                </div>
                              )}
                            </div>
                            <div>
                              <p className="text-white font-medium">{member.name}</p>
                              <p className="text-gray-400 text-sm">{member.country}</p>
                              {member.isLive && (
                                <p className="text-red-400 text-xs">🔴 Live - {member.viewers} viewers</p>
                              )}
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <Button
                              onClick={() => handleEditMember(member)}
                              variant="outline"
                              size="sm"
                            >
                              Edit
                            </Button>
                            <Button
                              onClick={() => handleDeleteMember(member.id)}
                              variant="destructive"
                              size="sm"
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* About Management */}
          <TabsContent value="about">
            <Card className="bg-gray-800 border-red-600/30">
              <CardHeader>
                <CardTitle className="text-white">About Page Content</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {!editingAbout ? (
                  <div>
                    <h3 className="text-xl text-white mb-2">{aboutContent.title}</h3>
                    <p className="text-gray-300 whitespace-pre-wrap">{aboutContent.content}</p>
                    <Button onClick={() => setEditingAbout(true)} className="mt-4 bg-red-600 hover:bg-red-700">
                      Edit About Content
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <input
                      type="text"
                      value={aboutForm.title}
                      onChange={(e) => setAboutForm(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="About title"
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                    />
                    <textarea
                      value={aboutForm.content}
                      onChange={(e) => setAboutForm(prev => ({ ...prev, content: e.target.value }))}
                      placeholder="About content"
                      className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white resize-none"
                      rows={8}
                    />
                    <div className="flex space-x-2">
                      <Button onClick={handleUpdateAbout} className="bg-green-600 hover:bg-green-700">
                        Save Changes
                      </Button>
                      <Button onClick={() => setEditingAbout(false)} variant="outline">
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contact Management */}
          <TabsContent value="contact">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-gray-800 border-red-600/30">
                <CardHeader>
                  <CardTitle className="text-white">Add Contact Item</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <input
                    type="text"
                    value={newContact.title}
                    onChange={(e) => setNewContact(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Contact title"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  />
                  <textarea
                    value={newContact.content}
                    onChange={(e) => setNewContact(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Contact description"
                    className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white resize-none"
                    rows={3}
                  />
                  <input
                    type="text"
                    value={newContact.link}
                    onChange={(e) => setNewContact(prev => ({ ...prev, link: e.target.value }))}
                    placeholder="Contact link (optional)"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  />
                  <Button onClick={handleCreateContact} className="w-full bg-red-600 hover:bg-red-700">
                    Add Contact
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-red-600/30">
                <CardHeader>
                  <CardTitle className="text-white">Contact Items</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {contactInfo.map((contact) => (
                      <div key={contact.id} className="bg-gray-700 p-4 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="text-white font-medium">{contact.title}</h3>
                            <p className="text-gray-300 text-sm mt-1">{contact.content}</p>
                            {contact.link && (
                              <a href={contact.link} className="text-red-400 text-sm hover:underline" target="_blank" rel="noopener noreferrer">
                                {contact.link}
                              </a>
                            )}
                          </div>
                          <Button
                            onClick={() => handleDeleteContact(contact.id)}
                            variant="destructive"
                            size="sm"
                          >
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Credits Management */}
          <TabsContent value="credits">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-gray-800 border-red-600/30">
                <CardHeader>
                  <CardTitle className="text-white">
                    {editingCredit ? 'Edit Credit' : 'Add New Credit'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <input
                    type="text"
                    value={newCredit.name}
                    onChange={(e) => setNewCredit(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Name (e.g., Johnny)"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  />
                  <input
                    type="text"
                    value={newCredit.role}
                    onChange={(e) => setNewCredit(prev => ({ ...prev, role: e.target.value }))}
                    placeholder="Role (e.g., Design, Programming, Moderation)"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  />
                  <textarea
                    value={newCredit.description}
                    onChange={(e) => setNewCredit(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Description (optional - what they contributed)"
                    className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white resize-none"
                    rows={3}
                  />
                  <div>
                    <label className="block text-white mb-2">Upload Photo (optional)</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'credit')}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-red-600 file:text-white hover:file:bg-red-700"
                    />
                  </div>
                  {newCredit.photo && (
                    <div className="text-center">
                      <img src={newCredit.photo} alt="Preview" className="w-20 h-20 rounded-full object-cover mx-auto border-2 border-red-600/50" />
                    </div>
                  )}
                  <div className="flex space-x-2">
                    {editingCredit ? (
                      <>
                        <Button onClick={handleUpdateCredit} className="flex-1 bg-green-600 hover:bg-green-700">
                          Update Credit
                        </Button>
                        <Button onClick={handleCancelCreditEdit} variant="outline" className="flex-1">
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button onClick={handleCreateCredit} className="w-full bg-red-600 hover:bg-red-700">
                        Add Credit
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-red-600/30">
                <CardHeader>
                  <CardTitle className="text-white">Current Credits</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {credits.map((credit) => (
                      <div key={credit.id} className="bg-gray-700 p-4 rounded-lg">
                        <div className="flex justify-between items-start">
                          <div className="flex items-start space-x-3">
                            {credit.photo && (
                              <img 
                                src={credit.photo} 
                                alt={credit.name} 
                                className="w-12 h-12 rounded-full object-cover border border-red-600/50" 
                              />
                            )}
                            <div>
                              <h3 className="text-white font-medium">
                                {credit.name} – {credit.role}
                              </h3>
                              {credit.description && (
                                <p className="text-gray-300 text-sm mt-1">{credit.description}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <Button
                              onClick={() => handleEditCredit(credit)}
                              variant="outline"
                              size="sm"
                            >
                              Edit
                            </Button>
                            <Button
                              onClick={() => handleDeleteCredit(credit.id)}
                              variant="destructive"
                              size="sm"
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Art Management */}
          <TabsContent value="art">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-gray-800 border-red-600/30">
                <CardHeader>
                  <CardTitle className="text-white">
                    {editingArt ? 'Edit Art Piece' : 'Add New Art Piece'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <input
                    type="text"
                    value={newArt.title}
                    onChange={(e) => setNewArt(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Art title"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  />
                  <textarea
                    value={newArt.description}
                    onChange={(e) => setNewArt(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Art description (optional)"
                    className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white resize-none"
                    rows={3}
                  />
                  <input
                    type="text"
                    value={newArt.artist_name}
                    onChange={(e) => setNewArt(prev => ({ ...prev, artist_name: e.target.value }))}
                    placeholder="Artist name"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  />
                  <input
                    type="text"
                    value={newArt.artist_twitter}
                    onChange={(e) => setNewArt(prev => ({ ...prev, artist_twitter: e.target.value }))}
                    placeholder="Artist Twitter handle (e.g., @username)"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  />
                  <div>
                    <label className="block text-white mb-2">Upload Art Image (required)</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'art')}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-red-600 file:text-white hover:file:bg-red-700"
                    />
                  </div>
                  {newArt.image && (
                    <div className="text-center">
                      <img src={newArt.image} alt="Preview" className="w-full max-h-48 object-cover rounded-lg border-2 border-red-600/50" />
                    </div>
                  )}
                  <div className="flex space-x-2">
                    {editingArt ? (
                      <>
                        <Button onClick={handleUpdateArt} className="flex-1 bg-green-600 hover:bg-green-700">
                          Update Art Piece
                        </Button>
                        <Button onClick={handleCancelArtEdit} variant="outline" className="flex-1">
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button onClick={handleCreateArt} className="w-full bg-red-600 hover:bg-red-700">
                        Add Art Piece
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-red-600/30">
                <CardHeader>
                  <CardTitle className="text-white">Art Gallery ({artPieces.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {artPieces.map((art) => (
                      <div key={art.id} className="bg-gray-700 p-4 rounded-lg">
                        <div className="flex items-start space-x-4">
                          <img
                            src={art.image}
                            alt={art.title}
                            className="w-20 h-20 object-cover rounded border border-red-600/50 flex-shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <h3 className="text-white font-medium">{art.title}</h3>
                            {art.description && (
                              <p className="text-gray-300 text-sm mt-1">{art.description}</p>
                            )}
                            <p className="text-red-400 text-sm mt-1">
                              by {art.artist_name} ({art.artist_twitter})
                            </p>
                          </div>
                          <div className="flex space-x-2">
                            <Button
                              onClick={() => handleEditArt(art)}
                              variant="outline"
                              size="sm"
                              className="border-gray-600 text-gray-300 hover:bg-gray-600"
                            >
                              Edit
                            </Button>
                            <Button
                              onClick={() => handleDeleteArt(art.id)}
                              variant="destructive"
                              size="sm"
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {artPieces.length === 0 && (
                      <div className="text-center py-8">
                        <p className="text-gray-400">No art pieces yet</p>
                        <p className="text-gray-500 text-sm mt-1">Add your first art piece to display in the gallery</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;